/**
 *
 * # track events:
 * import GtmService from 'static/script/app/ui/google-tagmanager-service';
 * GtmService.trackGtmEvent("zorro", { marvel: false, black: true })
 *
 *
 *
 */

import * as Sentry from '@sentry/browser';
import initData from '../../packages/init-data';

class GoogleTagManagerService {
    loadGtmScript() {
        this.newPage();

        const accountId = 'GTM-5TMB2D';
        (function(w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = '//www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', accountId);
    }

    push(payload) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
    }

    _getDatalayerObject() {
        let output = {};
        if (window.dataLayer) {
            for (let entry of window.dataLayer) {
                output = { ...output, ...entry };
            }
        }
        return output;
    }

    /**
     * destroy datalayer from previous loading. this is really only useful in spa pages, it's backwards compatible so it doesnt do any harm on old pages
     */
    _reset() {
        if (!window.dataLayer) {
            return;
        }

        let data = this._getDatalayerObject();
        for (let key of Object.keys(data)) {
            data[key] = undefined;
        }
        window.dataLayer.push({ ...data, event: 'reset' });
    }

    newPage(pageType = null) {
        this._reset();

        // init datalayer
        let initialDataLayer = {
            memberId: initData.memberId || 0,
            userLanguage: initData.languageCodeIso2,
            memberEmail: initData.isLoggedIn ? initData.memberEmail : '',
            memberHashedEmail: initData.isLoggedIn
                ? initData.memberEmailSha256
                : '',
            'criteo.hashedEmail': initData.isLoggedIn
                ? initData.memberEmailMd5
                : '',
            memberFirstName: initData.isLoggedIn
                ? initData.memberFirstName
                : '',
            memberLastName: initData.isLoggedIn ? initData.memberLastName : '',
            memberCountry: initData.isLoggedIn ? initData.memberCountry : '',
            loginState: initData.isLoggedIn ? 'logged in' : 'not logged in',
            isNotInIframe: window.self === window.top,
            'blueshift.event-api-key': initData.blueshiftEventApiKey,
            isSinglePageApp: initData.isSinglePageApp,
            isNativeAppContext: initData.isNativeAppContext,
            isQuantcastConsentEnabled:
                initData.featureSwitches['quantcast-consent'],
            quantcastSite: initData.quantcastSite
        };

        // add split tests to datalayer
        if (initData.splitTestGroups) {
            for (let [key, value] of Object.entries(initData.splitTestGroups)) {
                initialDataLayer[`splittest_${key}`] = value;
            }
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            ...initialDataLayer,
            event: 'initialDataLayer'
        });

        if (pageType) {
            this.push({ event: 'pageType', 'page.pageType': pageType });
        } else {
            // ------------------------------------------------
            // backwards compatibility
            // get old datalayer info and push it in
            if (window.legacyDataLayer) {
                for (let entry of window.legacyDataLayer) {
                    window.dataLayer.push({
                        event: 'legacyDataLayer',
                        ...entry
                    });
                }
            }
        }
    }

    /**
     * tracks a google analytics event
     * @param {string} category ga category
     * @param {string} action ga action what happens, ie: "Filter box - open/close"
     * @param {string} label ga label what the value of the action was, ie "close"
     * @param {integer} [value] ga interger value of the action.
     */
    trackAction(category, action, label, value = 0, nonInteractive = false) {
        this.push({
            event: 'trackEvent',
            eventCategory: category || '',
            eventAction: action || '',
            eventLabel: label || '',
            eventValue: value || '0',
            eventNonInteractive: nonInteractive
        });

        Sentry.addBreadcrumb({
            type: 'default',
            level: Sentry.Severity.Info,
            category: nonInteractive ? 'tracking' : 'ui-action',
            message: 'Analytics Event',
            data: {
                category,
                action,
                label,
                value
            }
        });
    }

    /**
     * google tag manager event. Note: if you want to send google analytics events use trackAction instead
     * @param {string} eventName
     * @param {object} [event] data
     */
    trackGtmEvent(eventName, data = {}) {
        this.push({
            event: eventName,
            ...data
        });
    }

    /**
     * Track a pageview. Use this script in SPA's to make sure that analytics gets pageview information.
     * This creates a virtual page view since google analytics by default doesn't track changes to url via
     * push state.
     */
    trackPageView() {
        this.trackGtmEvent('trackPageview');
    }

    /**
     *
     * @param {string} category
     * @param {string} action
     * @param {string} label
     * @param {string} callback
     * @param {} value
     */
    trackLinkClickAndCallback(category, action, label, callback, value = 0) {
        this.push({
            event: 'trackEvent',
            eventCategory: category || '',
            eventAction: action || '',
            eventLabel: label || '',
            eventValue: value || '0',
            eventNonInteractive: false,
            eventCallback: callback // part of google tag manager api to execute code after all tags in datalayer have been executed
        });
    }

    trackLinkClickAndGotoUrl(category, action, label, url, value = 0) {
        const callback = () => {
            location.href = url;
        };
        this.trackLinkClickAndCallback(
            category,
            action,
            label,
            callback,
            value
        );
    }
}

export default new GoogleTagManagerService();
